import React, { useEffect, useState } from 'react';
import apiClient, { formatApiError } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import * as PhosphorIcons from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const { ShieldCheck, Buildings, Eye, Check, X, Circle, ChartBar, Users, Storefront, MusicNotes, TrendUp, Pencil, Key, Copy, Gift, CalendarPlus, ClockCounterClockwise } = PhosphorIcons;

const AdminDashboard = () => {
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editTenant, setEditTenant] = useState(null);
  const [editLimits, setEditLimits] = useState({ users: 2, locations: 1 });
  const [pwResetResult, setPwResetResult] = useState(null); // {tenant_id, admin_email, temp_password, company_name}
  const [confirmResetTenant, setConfirmResetTenant] = useState(null);
  const [resetting, setResetting] = useState(false);
  // Grant-subscription modal state (comp-free access for a tenant, set custom expiry)
  const [grantTenant, setGrantTenant] = useState(null);
  const [grantForm, setGrantForm] = useState({ users: 2, locations: 1, expiry_date: '', status: 'active', note: '' });
  const [granting, setGranting] = useState(false);
  // Audit log — grants made by global admins (for accounting / review)
  const [grantsLog, setGrantsLog] = useState([]);
  const [grantsOpen, setGrantsOpen] = useState(false);
  const [grantsLoading, setGrantsLoading] = useState(false);
  const { user, impersonate } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { fetchKpi(); }, []);

  const fetchKpi = async () => {
    try {
      const res = await apiClient.get('/admin/kpi');
      setKpi(res.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const loadGrantsLog = async () => {
    setGrantsLoading(true);
    try {
      const res = await apiClient.get('/admin/grants-log');
      setGrantsLog(res.data?.entries || []);
      setGrantsOpen(true);
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setGrantsLoading(false); }
  };

  const handleImpersonate = async (tenantId) => {
    const result = await impersonate(tenantId);
    if (result.success) {
      toast.success(result.data.message);
      navigate('/dashboard/');
    } else {
      toast.error(result.error);
    }
  };

  const handleResetAdminPassword = async () => {
    if (!confirmResetTenant) return;
    setResetting(true);
    try {
      const res = await apiClient.post(`/admin/tenants/${confirmResetTenant.id}/reset-admin-password`);
      setPwResetResult({
        ...res.data,
        company_name: confirmResetTenant.company_name,
      });
      setConfirmResetTenant(null);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setResetting(false);
    }
  };

  const copyTempPassword = async () => {
    if (!pwResetResult?.temp_password) return;
    try {
      await navigator.clipboard.writeText(pwResetResult.temp_password);
      toast.success('Password copied to clipboard');
    } catch {
      toast.error('Copy failed — select and copy manually');
    }
  };

  const openEditLimits = (tenant) => {
    setEditTenant(tenant);
    setEditLimits({
      users: tenant.plan?.users || 2,
      locations: tenant.plan?.locations || 1,
    });
    setEditOpen(true);
  };

  const handleSaveLimits = async () => {
    try {
      await apiClient.patch(`/admin/tenants/${editTenant.id}/limits`, editLimits);
      toast.success('Tenant limits updated');
      setEditOpen(false);
      fetchKpi();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const openGrant = (tenant) => {
    // Prefill from current plan + 30 days from today as default
    const defaultExpiry = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    setGrantForm({
      users: tenant.plan?.users || 2,
      locations: tenant.plan?.locations || 1,
      expiry_date: defaultExpiry.toISOString().slice(0, 16), // yyyy-MM-ddTHH:mm for datetime-local input
      status: 'active',
      note: '',
    });
    setGrantTenant(tenant);
  };

  const handleGrant = async () => {
    if (!grantTenant) return;
    setGranting(true);
    try {
      await apiClient.post(`/admin/tenants/${grantTenant.id}/grant-subscription`, {
        users: Number(grantForm.users),
        locations: Number(grantForm.locations),
        expiry_date: new Date(grantForm.expiry_date).toISOString(),
        status: grantForm.status,
        note: grantForm.note,
      });
      toast.success(`Subscription granted to ${grantTenant.company_name || grantTenant.email}`);
      setGrantTenant(null);
      fetchKpi();
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setGranting(false); }
  };

  const handleToggleStatus = async (tenantId, currentStatus) => {
    const newStatus = currentStatus === 'blocked' ? 'active' : 'blocked';
    try {
      await apiClient.patch(`/admin/tenants/${tenantId}/limits`, { status: newStatus });
      toast.success(`Tenant ${newStatus === 'active' ? 'activated' : 'blocked'}`);
      fetchKpi();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  if (user?.role !== 'global_super_admin') {
    return <div className="text-red-500 font-mono text-center p-8" data-testid="admin-access-denied">Access denied.</div>;
  }
  if (loading) return <div className="text-[#6b665c] font-mono">Loading...</div>;
  if (!kpi) return <div className="text-[#8a8477] font-mono">No data</div>;

  const t = kpi.totals;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="text-4xl font-bold tracking-tight mb-2" data-testid="admin-title">
          <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">Platform Command</span>
        </h1>
        <p className="text-[#6b665c] font-mono text-sm tracking-wider uppercase">Global KPI &amp; Tenant Management</p>
        <div className="mt-3">
          <Button
            onClick={loadGrantsLog}
            className="bg-[#f5f0e8] hover:bg-[#e4ebe5] text-[#5a6e5f] border border-[#c5d4c8] h-8 px-3 text-xs"
            data-testid="open-grants-log-btn"
          >
            <ClockCounterClockwise size={14} className="mr-1" />
            {grantsLoading ? 'Loading…' : 'Grants audit log'}
          </Button>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="kpi-grid">
        <KpiCard label="Total Tenants" value={t.tenants} icon={<Buildings size={22} className="text-blue-400" />} border="border-blue-500/20" />
        <KpiCard label="Active / Trial" value={`${t.active} / ${t.trial}`} icon={<Check size={22} className="text-green-400" />} border="border-green-500/20" />
        <KpiCard label="Blocked" value={t.blocked} icon={<X size={22} className="text-red-400" />} border="border-red-500/20" />
        <KpiCard label="Total Users" value={t.users} icon={<Users size={22} className="text-purple-400" />} border="border-purple-500/20" />
        <KpiCard label="Total Stores" value={t.stores} icon={<Storefront size={22} className="text-cyan-400" />} border="border-cyan-500/20" />
        <KpiCard label="Online Now" value={t.online_stores} icon={<TrendUp size={22} className="text-green-400" />} border="border-green-500/20" />
        <KpiCard label="Total Tracks" value={t.tracks} icon={<MusicNotes size={22} className="text-pink-400" />} border="border-pink-500/20" />
        <KpiCard label="Subscription Rev" value={`${t.active} paid`} icon={<ChartBar size={22} className="text-amber-400" />} border="border-amber-500/20" />
      </div>

      {/* Edit Limits Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-white border-[#c5d4c8] text-[#2d2a26]">
          <DialogHeader>
            <DialogTitle className="font-mono flex items-center gap-2">
              <Pencil size={18} className="text-amber-400" />
              Edit Limits — {editTenant?.company_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-mono text-xs uppercase text-[#6b665c]">Max Users</Label>
              <Input
                type="number"
                min={1}
                value={editLimits.users}
                onChange={(e) => setEditLimits({ ...editLimits, users: parseInt(e.target.value) || 1 })}
                className="mt-1 bg-[#f5f0e8] border-[#c5d4c8]"
                data-testid="edit-max-users"
              />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase text-[#6b665c]">Max Locations</Label>
              <Input
                type="number"
                min={1}
                value={editLimits.locations}
                onChange={(e) => setEditLimits({ ...editLimits, locations: parseInt(e.target.value) || 1 })}
                className="mt-1 bg-[#f5f0e8] border-[#c5d4c8]"
                data-testid="edit-max-locations"
              />
            </div>
            <Button onClick={handleSaveLimits} className="w-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-limits-btn">
              Save Limits
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tenants Table */}
      <div className="bg-white border border-[#c5d4c8] rounded-md overflow-hidden">
        <div className="px-6 py-4 border-b border-[#c5d4c8] flex items-center gap-2">
          <ShieldCheck size={20} className="text-amber-400" />
          <h2 className="text-lg font-bold">Tenant Management</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="tenants-table">
            <thead>
              <tr className="border-b border-[#c5d4c8] text-left">
                <th className="px-4 py-3 text-xs text-[#8a8477] font-mono uppercase">Company</th>
                <th className="px-4 py-3 text-xs text-[#8a8477] font-mono uppercase">Status</th>
                <th className="px-4 py-3 text-xs text-[#8a8477] font-mono uppercase">Users</th>
                <th className="px-4 py-3 text-xs text-[#8a8477] font-mono uppercase">Stores</th>
                <th className="px-4 py-3 text-xs text-[#8a8477] font-mono uppercase">Tracks</th>
                <th className="px-4 py-3 text-xs text-[#8a8477] font-mono uppercase">Limits</th>
                <th className="px-4 py-3 text-xs text-[#8a8477] font-mono uppercase text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {kpi.tenants.map((tenant) => {
                const sc = tenant.status === 'active' ? 'text-green-400 bg-green-900/20' :
                           tenant.status === 'trial' ? 'text-blue-400 bg-blue-900/20' :
                           'text-red-400 bg-[#fce8eb]/20';
                return (
                  <tr key={tenant.id} className="border-b border-[#c5d4c8]/50 hover:bg-[#e4ebe5]/30 transition-colors" data-testid={`tenant-row-${tenant.id}`}>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium">{tenant.company_name}</p>
                      <p className="text-xs text-[#8a8477] font-mono">{tenant.contact_name}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-mono ${sc}`}>
                        <Circle size={6} weight="fill" />
                        {tenant.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-[#5a6e5f]">
                      {tenant.usage.users} <span className="text-[#8a8477]">/ {tenant.plan?.users || '?'}</span>
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-[#5a6e5f]">
                      {tenant.usage.stores} <span className="text-[#8a8477]">/ {tenant.plan?.locations || '?'}</span>
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-[#5a6e5f]">{tenant.usage.tracks}</td>
                    <td className="px-4 py-3">
                      <Button
                        onClick={() => openEditLimits(tenant)}
                        className="bg-[#e4ebe5] hover:bg-[#d9d3c5] text-[#5a6e5f] border border-[#c5d4c8] h-7 px-2 text-xs"
                        data-testid={`edit-limits-${tenant.id}`}
                      >
                        <Pencil size={12} className="mr-1" /> Edit
                      </Button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center gap-2 justify-end">
                        <Button
                          onClick={() => handleToggleStatus(tenant.id, tenant.status)}
                          className={`h-7 px-2 text-xs font-mono ${tenant.status === 'blocked' ? 'bg-green-900/20 text-green-400 border-green-800/30' : 'bg-[#fce8eb]/20 text-red-400 border-red-800/30'} border`}
                          data-testid={`toggle-status-${tenant.id}`}
                        >
                          {tenant.status === 'blocked' ? 'Activate' : 'Block'}
                        </Button>
                        <Button
                          onClick={() => setConfirmResetTenant(tenant)}
                          className="bg-amber-900/10 hover:bg-amber-900/20 text-amber-700 border border-amber-300 h-7 px-2 text-xs"
                          data-testid={`reset-admin-pw-btn-${tenant.id}`}
                          title="Reset tenant admin password"
                        >
                          <Key size={12} className="mr-1" /> Reset PW
                        </Button>
                        <Button
                          onClick={() => openGrant(tenant)}
                          className="bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-700 border border-emerald-600/30 h-7 px-2 text-xs"
                          data-testid={`grant-subscription-btn-${tenant.id}`}
                          title="Grant ₹499 access + set expiry without payment"
                        >
                          <Gift size={12} className="mr-1" /> Grant
                        </Button>
                        <Button
                          onClick={() => handleImpersonate(tenant.id)}
                          className="bg-amber-600/20 hover:bg-amber-600/30 text-amber-400 border border-amber-600/30 h-7 px-2 text-xs"
                          data-testid={`impersonate-btn-${tenant.id}`}
                        >
                          <Eye size={12} className="mr-1" /> View
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Confirm reset admin password */}
      <Dialog open={!!confirmResetTenant} onOpenChange={(o) => { if (!o) setConfirmResetTenant(null); }}>
        <DialogContent className="bg-white border-[#c5d4c8] text-[#2d2a26]">
          <DialogHeader>
            <DialogTitle className="font-mono">Reset tenant admin password?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#2d2a26]">
              This generates a strong temporary password for <strong>{confirmResetTenant?.company_name}</strong> and replaces their current password immediately.
              The tenant admin will be logged out and will need to use the new password to sign in.
            </p>
            <p className="text-xs text-[#8a8477] font-mono">Use this only with the tenant's explicit consent.</p>
            <div className="flex gap-2">
              <Button
                onClick={() => setConfirmResetTenant(null)}
                className="flex-1 bg-[#f5f0e8] hover:bg-[#e4ebe5] text-[#5a6e5f] border border-[#c5d4c8]"
                data-testid="cancel-reset-admin-pw"
              >
                Cancel
              </Button>
              <Button
                onClick={handleResetAdminPassword}
                disabled={resetting}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="confirm-reset-admin-pw"
              >
                {resetting ? 'Generating…' : 'Generate & replace'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Show temporary password once */}
      <Dialog open={!!pwResetResult} onOpenChange={(o) => { if (!o) setPwResetResult(null); }}>
        <DialogContent className="bg-white border-[#c5d4c8] text-[#2d2a26]">
          <DialogHeader>
            <DialogTitle className="font-mono flex items-center gap-2"><Key size={18} className="text-amber-500" />Temporary password generated</DialogTitle>
          </DialogHeader>
          {pwResetResult && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm">
                Copy this password now — <strong>it will not be shown again</strong>. Share it with the tenant admin through a secure channel.
              </div>
              <div>
                <p className="text-xs text-[#8a8477] font-mono mb-1">Tenant</p>
                <p className="text-sm font-medium" data-testid="pw-reset-company">{pwResetResult.company_name}</p>
                <p className="text-xs text-[#6b665c] font-mono" data-testid="pw-reset-email">{pwResetResult.admin_email}</p>
              </div>
              <div>
                <p className="text-xs text-[#8a8477] font-mono mb-1">Temporary password</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-[#f5f0e8] border border-[#c5d4c8] rounded px-3 py-2 text-sm font-mono break-all" data-testid="pw-reset-temp-password">
                    {pwResetResult.temp_password}
                  </code>
                  <Button
                    onClick={copyTempPassword}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="copy-temp-password"
                  >
                    <Copy size={14} className="mr-1" />Copy
                  </Button>
                </div>
              </div>
              <Button
                onClick={() => setPwResetResult(null)}
                className="w-full bg-[#f5f0e8] hover:bg-[#e4ebe5] text-[#5a6e5f] border border-[#c5d4c8]"
                data-testid="close-pw-reset-result"
              >
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Grant subscription modal — Global super-admin only (route already gated) */}
      <Dialog open={!!grantTenant} onOpenChange={(o) => { if (!o) setGrantTenant(null); }}>
        <DialogContent className="bg-white border-[#c5d4c8] text-[#2d2a26] max-w-lg" data-testid="grant-subscription-modal">
          <DialogHeader>
            <DialogTitle className="font-mono flex items-center gap-2">
              <Gift size={18} className="text-emerald-600" /> Grant subscription
            </DialogTitle>
          </DialogHeader>
          {grantTenant && (
            <div className="space-y-4">
              <div className="bg-[#f5f0e8] border border-[#e4ebe5] rounded-md p-3">
                <p className="text-xs text-[#6b665c] font-mono uppercase">Tenant</p>
                <p className="text-sm font-medium" data-testid="grant-tenant-name">{grantTenant.company_name}</p>
                <p className="text-xs text-[#6b665c] font-mono">{grantTenant.email}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-[#6b665c]">Users (total)</Label>
                  <Input
                    type="number" min="2"
                    value={grantForm.users}
                    onChange={(e) => setGrantForm({ ...grantForm, users: e.target.value })}
                    className="mt-2"
                    data-testid="grant-users-input"
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-wider text-[#6b665c]">Locations (total)</Label>
                  <Input
                    type="number" min="1"
                    value={grantForm.locations}
                    onChange={(e) => setGrantForm({ ...grantForm, locations: e.target.value })}
                    className="mt-2"
                    data-testid="grant-locations-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-[#6b665c]">
                  <CalendarPlus size={12} className="inline mr-1" />
                  Subscription renewal (expiry) date
                </Label>
                <Input
                  type="datetime-local"
                  value={grantForm.expiry_date}
                  onChange={(e) => setGrantForm({ ...grantForm, expiry_date: e.target.value })}
                  className="mt-2"
                  data-testid="grant-expiry-input"
                />
                <p className="text-[11px] text-[#8a8477] mt-1 font-mono">Can be changed anytime by granting again.</p>
              </div>

              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-[#6b665c]">Internal note (optional)</Label>
                <Input
                  placeholder="e.g., complimentary extension — board demo deal"
                  value={grantForm.note}
                  onChange={(e) => setGrantForm({ ...grantForm, note: e.target.value })}
                  className="mt-2"
                  data-testid="grant-note-input"
                  maxLength={500}
                />
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <Button variant="outline" onClick={() => setGrantTenant(null)} disabled={granting} data-testid="grant-cancel-btn">
                  Cancel
                </Button>
                <Button
                  onClick={handleGrant}
                  disabled={granting || !grantForm.expiry_date}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="grant-confirm-btn"
                >
                  {granting ? 'Granting...' : 'Grant access (no charge)'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Grants audit log */}
      <Dialog open={grantsOpen} onOpenChange={setGrantsOpen}>
        <DialogContent className="bg-white border-[#c5d4c8] text-[#2d2a26] max-w-4xl" data-testid="grants-log-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono flex items-center gap-2">
              <ClockCounterClockwise size={18} className="text-[#7c9082]" />
              Grants audit log
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#6b665c] font-mono">
              Every complimentary grant made via <em>Grant subscription</em>. Sorted most-recent first. Shows {grantsLog.length} record{grantsLog.length === 1 ? '' : 's'}.
            </p>
            <div className="overflow-x-auto max-h-[60vh]">
              {grantsLog.length === 0 ? (
                <p className="text-sm text-[#8a8477] font-mono text-center py-8">No grants recorded yet.</p>
              ) : (
                <table className="w-full text-sm" data-testid="grants-log-table">
                  <thead>
                    <tr className="border-b border-[#c5d4c8] text-left">
                      <th className="px-3 py-2 text-xs text-[#8a8477] font-mono uppercase">Tenant</th>
                      <th className="px-3 py-2 text-xs text-[#8a8477] font-mono uppercase">Granted by</th>
                      <th className="px-3 py-2 text-xs text-[#8a8477] font-mono uppercase">When</th>
                      <th className="px-3 py-2 text-xs text-[#8a8477] font-mono uppercase">Expires</th>
                      <th className="px-3 py-2 text-xs text-[#8a8477] font-mono uppercase">Plan</th>
                      <th className="px-3 py-2 text-xs text-[#8a8477] font-mono uppercase">Status</th>
                      <th className="px-3 py-2 text-xs text-[#8a8477] font-mono uppercase">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grantsLog.map((g) => (
                      <tr key={`${g.tenant_id}-${g.granted_at}`} className="border-b border-[#c5d4c8]/50" data-testid={`grants-log-row-${g.tenant_id}`}>
                        <td className="px-3 py-2">
                          <p className="text-sm font-medium">{g.company_name || '—'}</p>
                          <p className="text-[11px] text-[#8a8477] font-mono">{g.contact_email || ''}</p>
                        </td>
                        <td className="px-3 py-2 text-xs font-mono text-[#5a6e5f]">{g.granted_by || '—'}</td>
                        <td className="px-3 py-2 text-xs font-mono text-[#5a6e5f]">
                          {g.granted_at ? new Date(g.granted_at).toLocaleString() : '—'}
                        </td>
                        <td className="px-3 py-2 text-xs font-mono text-[#5a6e5f]">
                          {g.end_date ? new Date(g.end_date).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-3 py-2 text-xs font-mono text-[#5a6e5f]">
                          {g.plan?.users || '?'} users · {g.plan?.locations || '?'} loc
                        </td>
                        <td className="px-3 py-2 text-xs font-mono text-[#5a6e5f]">{g.status || '—'}</td>
                        <td className="px-3 py-2 text-xs font-mono text-[#5a6e5f] max-w-xs truncate" title={g.note || ''}>
                          {g.note || <span className="text-[#b3ac9f]">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const KpiCard = ({ label, value, icon, border }) => (
  <div className={`bg-white border ${border} rounded-md p-5`}>
    <div className="flex items-center justify-between mb-2">{icon}</div>
    <p className="text-2xl font-bold font-mono">{value}</p>
    <p className="text-xs text-[#8a8477] font-mono uppercase tracking-wider mt-1">{label}</p>
  </div>
);

export default AdminDashboard;
